import React from 'react';
import { Anime, BroadcastDay, BROADCAST_DAYS, SEASON_CONFIGS, TIER_CONFIGS } from '../types/anime';
import {
  formatImageUrl,
  getTodayBroadcastDay,
  getAnimePlatformSlots,
  getAnimeScheduleSubtitle,
  getDayConfig,
  isOnBreak,
} from '../utils/helpers';
import { AnimeCard } from './AnimeCard';
import { MalScoreBadge } from './MalScoreBadge';
import { EpisodeCounts } from './EpisodeProgress';
import {
  Bell,
  BellOff,
  ExternalLink,
  Plus,
  Send,
  Sparkles,
  HelpCircle,
  CheckCircle2,
  Pencil,
  LayoutGrid,
  List,
  GitMerge,
  Loader2,
  PauseCircle,
} from 'lucide-react';

interface ScheduleBoardProps {
  animeList: Anime[];
  onQuickPlus: (id: string) => void;
  onQuickMinus?: (id: string) => void;
  onToggleNotify: (id: string) => void;
  onChangeBroadcastDay: (id: string, day: BroadcastDay) => void;
  onEdit: (anime: Anime) => void;
  onOpenDetail: (anime: Anime) => void;
  onCheckUpdates: () => void;
  isCheckingUpdates: boolean;
  onNotifyTodayToLine: () => void;
  isSendingLine: boolean;
  onMergeDuplicates?: () => void;
}

const byTime = (a: Anime, b: Anime) =>
  (a.broadcastTime || '99:99').localeCompare(b.broadcastTime || '99:99');

/** Small A / B / W / R squares, only for the platforms this show is on */
const PlatformDots: React.FC<{ anime: Anime }> = ({ anime }) => {
  const slots = getAnimePlatformSlots(anime);
  const platforms = [
    { key: 'A', name: 'Ani-One', slot: slots.aniOne, on: 'bg-red-500/90 text-white' },
    { key: 'B', name: 'Bilibili', slot: slots.bilibili, on: 'bg-sky-500/90 text-white' },
    { key: 'W', name: 'Anime-Waku', slot: slots.waku, on: 'bg-teal-500/90 text-white' },
    { key: 'R', name: 'AnimeRuka', slot: slots.ruka, on: 'bg-orange-500/90 text-white' },
  ];
  return (
    <div className="flex items-center gap-[3px]">
      {platforms.map(({ key, name, slot, on }) =>
        slot ? (
          <a
            key={key}
            href={slot.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`เปิดดูบน ${name}`}
            className={`w-[18px] h-[18px] rounded-[5px] grid place-items-center text-[9.5px] font-bold hover:brightness-125 transition ${on}`}
          >
            {key}
          </a>
        ) : null
      )}
    </div>
  );
};

const BellToggle: React.FC<{ anime: Anime; onToggle: (id: string) => void }> = ({
  anime,
  onToggle,
}) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onToggle(anime.id);
    }}
    title={anime.notifyLine ? 'เปิดแจ้งเตือน LINE อยู่ (คลิกเพื่อปิด)' : 'ปิดแจ้งเตือน LINE อยู่ (คลิกเพื่อเปิด)'}
    className={`w-7 h-7 rounded-full grid place-items-center shrink-0 transition-all active:scale-90 ${
      anime.notifyLine
        ? 'bg-[#06C755]/15 text-[#4ef08f]'
        : 'bg-white/[0.04] text-slate-500 hover:text-white'
    }`}
  >
    {anime.notifyLine ? <Bell className="w-3.5 h-3.5 fill-current" /> : <BellOff className="w-3.5 h-3.5" />}
  </button>
);

/** "🍁 2026" — the show's season and year */
const SeasonTag: React.FC<{ anime: Anime }> = ({ anime }) => {
  const cfg = SEASON_CONFIGS[anime.season] || SEASON_CONFIGS.FALL;
  return (
    <span title={`${cfg.nameTh} ${anime.year}`} className="text-slate-400 whitespace-nowrap">
      {cfg.icon} {anime.year}
    </span>
  );
};

const Thumb: React.FC<{ anime: Anime; className?: string }> = ({ anime, className = 'w-12 h-16' }) => (
  <img
    src={formatImageUrl(anime.imageUrl)}
    alt={anime.title}
    loading="lazy"
    decoding="async"
    referrerPolicy="no-referrer"
    className={`${className} object-cover rounded-xl shrink-0 bg-slate-900 ring-1 ring-white/10`}
  />
);

export const ScheduleBoard: React.FC<ScheduleBoardProps> = ({
  animeList,
  onQuickPlus,
  onQuickMinus,
  onToggleNotify,
  onChangeBroadcastDay,
  onEdit,
  onOpenDetail,
  onNotifyTodayToLine,
  isSendingLine,
  onMergeDuplicates,
}) => {
  const [cardMode, setCardMode] = React.useState<'poster' | 'compact'>('poster');
  const todayId = getTodayBroadcastDay();
  const todayConfig = BROADCAST_DAYS.find((d) => d.id === todayId)!;
  // Phones show one day at a time; desktop shows the whole week side by side
  const [mobileDay, setMobileDay] = React.useState<BroadcastDay>(
    todayId === 'UNKNOWN' ? 'MON' : todayId
  );

  const weekDays = BROADCAST_DAYS.filter((d) => d.id !== 'UNKNOWN');
  // Finished shows have no more episodes coming: off the schedule, listed on their own at the bottom
  const airing = animeList.filter((a) => a.status !== 'จบแล้ว');
  const behindOf = (a: Anime) => Math.max(0, (Number(a.latestEp) || 0) - (Number(a.watchedEp) || 0));
  // Ones with episodes still to watch first
  const finishedAnime = animeList
    .filter((a) => a.status === 'จบแล้ว')
    .sort((a, b) => Number(behindOf(b) > 0) - Number(behindOf(a) > 0) || a.title.localeCompare(b.title));
  // Skipping this week on AnimeRuka ("สัปดาห์นี้งดฉาย / พักฉาย"): off the week, listed on their own
  const onBreakAnime = airing
    .filter((a) => isOnBreak(a))
    .sort((a, b) => (a.onBreak?.returnOn || '9999').localeCompare(b.onBreak?.returnOn || '9999'));
  const scheduled = airing.filter((a) => !isOnBreak(a));
  const unknownDayAnime = scheduled.filter((a) => a.broadcastDay === 'UNKNOWN');
  const todayAnime = scheduled
    .filter((a) => a.broadcastDay === todayId)
    .sort(byTime);
  const dayItemsOf = (day: BroadcastDay) =>
    scheduled.filter((a) => a.broadcastDay === day).sort(byTime);

  const toolBtn =
    'h-8 px-2.5 rounded-full text-xs font-medium flex items-center gap-1.5 bg-white/[0.04] hover:bg-white/[0.09] text-slate-300 hover:text-white border border-white/[0.07] transition-all active:scale-95';

  return (
    <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5 pb-8">
      {/* Today */}
      <section className="relative glass-panel rounded-3xl p-4 sm:p-5 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(18rem_12rem_at_100%_0%,rgba(244,63,94,0.16),transparent_70%)] pointer-events-none" />
        <div className="relative flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-rose-300 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
              {todayConfig.nameTh}
            </div>
            <h2 className="text-lg sm:text-2xl font-semibold text-white mt-0.5">
              {todayAnime.length > 0 ? `ฉายวันนี้ ${todayAnime.length} เรื่อง` : 'วันนี้ไม่มีเรื่องที่ฉาย'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onNotifyTodayToLine}
            disabled={isSendingLine || todayAnime.length === 0}
            title="ส่งตารางฉายวันนี้เข้า LINE"
            className="h-9 px-3.5 rounded-full bg-[#06C755] hover:bg-[#05b34c] disabled:opacity-40 text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 shrink-0 shadow-lg shadow-[#06C755]/20 active:scale-95 transition-all"
          >
            {isSendingLine ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span>ส่งเข้า LINE</span>
          </button>
        </div>

        {todayAnime.length > 0 && (
          <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0 mt-4 flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 overflow-x-auto no-scrollbar snap-x">
            {todayAnime.map((anime) => {
              const sched = getAnimeScheduleSubtitle(anime);
              return (
                <div
                  key={anime.id}
                  onClick={() => onOpenDetail(anime)}
                  className="snap-start shrink-0 w-[78%] sm:w-auto cursor-pointer rounded-2xl p-2.5 flex items-center gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-colors"
                >
                  <Thumb anime={anime} className="w-14 h-[74px]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="px-1.5 py-[3px] rounded-md bg-rose-500 text-white font-semibold leading-none">
                        {sched.timeStr}
                      </span>
                      {sched.isEstimated && <span className="text-slate-500">โดยประมาณ</span>}
                      <span className="ml-auto"><SeasonTag anime={anime} /></span>
                    </div>
                    <h4 className="text-[13px] font-medium text-white line-clamp-1 mt-1.5">
                      {anime.title}
                    </h4>
                    <div className="flex items-center justify-between gap-2 mt-1.5">
                      <span className="text-[11px]">
                        <EpisodeCounts anime={anime} />
                      </span>
                      <PlatformDots anime={anime} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Week */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base sm:text-lg font-semibold text-white">ตารางรายสัปดาห์</h3>
          <div className="flex items-center gap-1.5">
            {onMergeDuplicates && (
              <button
                type="button"
                onClick={onMergeDuplicates}
                title="รวมอนิเมะที่มีหลายเว็บเข้าด้วยกันเป็นการ์ดเดียว"
                className={toolBtn}
              >
                <GitMerge className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">รวมเรื่องซ้ำ</span>
              </button>
            )}
            <div className="flex items-center h-8 p-0.5 rounded-full bg-white/[0.04] border border-white/[0.07]">
              {(
                [
                  ['poster', LayoutGrid, 'แบบโปสเตอร์'],
                  ['compact', List, 'แบบรายการ'],
                ] as const
              ).map(([mode, Icon, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setCardMode(mode)}
                  title={label}
                  className={`h-full w-8 rounded-full grid place-items-center transition-all ${
                    cardMode === mode ? 'bg-fuchsia-500/90 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Day picker (phones / tablets) */}
        <div className="lg:hidden -mx-3 px-3 sm:mx-0 sm:px-0 flex gap-1.5 overflow-x-auto no-scrollbar">
          {weekDays.map((day) => {
            const active = mobileDay === day.id;
            const count = dayItemsOf(day.id).length;
            return (
              <button
                key={day.id}
                type="button"
                onClick={() => setMobileDay(day.id)}
                className={`relative shrink-0 w-[3.25rem] py-2 rounded-2xl flex flex-col items-center gap-0.5 border transition-all active:scale-95 ${
                  active
                    ? 'bg-white text-slate-900 border-white'
                    : 'bg-white/[0.03] text-slate-300 border-white/[0.07]'
                }`}
              >
                <span className="text-sm font-semibold leading-none">{day.shortTh.replace('.', '')}</span>
                <span className={`text-[10px] leading-none ${active ? 'text-slate-500' : 'text-slate-500'}`}>
                  {count} เรื่อง
                </span>
                {day.id === todayId && (
                  <span className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-500" />
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-7 gap-3">
          {weekDays.map((day) => {
            const isToday = day.id === todayId;
            const dayItems = dayItemsOf(day.id);

            return (
              <div
                key={day.id}
                className={`${day.id === mobileDay ? 'flex' : 'hidden lg:flex'} flex-col lg:rounded-3xl lg:p-2 ${
                  isToday ? 'lg:bg-rose-500/[0.05] lg:ring-1 lg:ring-rose-400/30' : 'lg:glass-panel'
                }`}
              >
                {/* Column header (desktop) */}
                <div className="hidden lg:flex items-center justify-between px-1.5 pt-1 pb-2.5">
                  <span className={`text-sm font-semibold ${isToday ? 'text-rose-200' : 'text-slate-200'}`}>
                    {day.nameTh.replace('วัน', '')}
                  </span>
                  <span className="text-[11px] text-slate-500 tabular-nums">{dayItems.length}</span>
                </div>

                {dayItems.length === 0 ? (
                  <div className="py-6 rounded-2xl border border-dashed border-white/[0.07] text-center text-xs text-slate-600">
                    ไม่มีคิวฉาย
                  </div>
                ) : cardMode === 'poster' ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-1 gap-2 sm:gap-3 lg:gap-2">
                    {dayItems.map((anime) => (
                      <AnimeCard
                        key={anime.id}
                        anime={anime}
                        isSortMode={false}
                        showGrade
                        onQuickPlus={onQuickPlus}
                        onQuickMinus={onQuickMinus || (() => {})}
                        onToggleNotify={onToggleNotify}
                        onEdit={onEdit}
                        onOpenDetail={onOpenDetail}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                    {dayItems.map((anime) => {
                      const sched = getAnimeScheduleSubtitle(anime);
                      return (
                        <div
                          key={anime.id}
                          onClick={() => onOpenDetail(anime)}
                          className="cursor-pointer rounded-2xl p-2 flex gap-2.5 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-colors"
                        >
                          <Thumb anime={anime} className="w-11 h-[60px] lg:w-10 lg:h-[54px]" />
                          <div className="min-w-0 flex-1 flex flex-col justify-between">
                            <div className="flex items-center gap-1.5 text-[11px]">
                              <span className="text-amber-300 font-semibold tabular-nums">{sched.timeStr}</span>
                              <span
                                className={`px-1 rounded text-[9.5px] font-bold text-white bg-gradient-to-br ${TIER_CONFIGS[anime.grade].gradient}`}
                              >
                                {anime.grade}
                              </span>
                              <span className="ml-auto text-[10px]"><SeasonTag anime={anime} /></span>
                            </div>
                            <div className="text-[12px] font-medium text-slate-100 line-clamp-1 lg:line-clamp-2 leading-snug">
                              {anime.title}
                            </div>
                            <div className="flex items-center justify-between gap-1 text-[10.5px]">
                              <EpisodeCounts anime={anime} />
                            </div>
                          </div>
                          <div className="flex flex-col items-center justify-between lg:hidden">
                            <BellToggle anime={anime} onToggle={onToggleNotify} />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onQuickPlus(anime.id);
                              }}
                              title="เพิ่มตอนที่ดูแล้ว (+1)"
                              className="w-7 h-7 rounded-full grid place-items-center bg-fuchsia-500/80 hover:bg-fuchsia-400 text-white active:scale-90 transition-all"
                            >
                              <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* On break this week */}
      {onBreakAnime.length > 0 && (
        <section className="glass-panel rounded-3xl p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-xl grid place-items-center bg-amber-500/10 text-amber-300 shrink-0">
              <PauseCircle className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-white">
                สัปดาห์นี้งดฉาย / พักฉาย
                <span className="ml-2 text-sm font-normal text-slate-500">{onBreakAnime.length} เรื่อง</span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                ตามตาราง AnimeRuka · ไม่แจ้งเตือนเวลาฉายจนกว่าจะกลับมา
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-4">
            {onBreakAnime.map((anime) => {
              const primarySource = anime.sources?.[0];
              const behind = behindOf(anime);
              const usualSlot =
                anime.broadcastDay === 'UNKNOWN'
                  ? ''
                  : `ปกติ ${getDayConfig(anime.broadcastDay).shortTh}${anime.broadcastTime ? ` ${anime.broadcastTime}` : ''}`;
              return (
                <div
                  key={anime.id}
                  onClick={() => onOpenDetail(anime)}
                  className="cursor-pointer rounded-2xl p-2.5 flex gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-colors"
                >
                  <Thumb anime={anime} className="w-12 h-16" />
                  <div className="min-w-0 flex-1 flex flex-col justify-between gap-1">
                    <div className="flex items-start gap-1.5">
                      <h4 className="text-[13px] font-medium text-white line-clamp-1 flex-1">{anime.title}</h4>
                      <span
                        className={`px-1 rounded text-[9.5px] font-bold text-white bg-gradient-to-br shrink-0 ${TIER_CONFIGS[anime.grade].gradient}`}
                      >
                        {anime.grade}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] flex-wrap">
                      <span className="px-1.5 py-[3px] rounded-full bg-amber-500 text-slate-950 font-semibold leading-none whitespace-nowrap">
                        ⏸ {anime.onBreak!.label}
                      </span>
                      {usualSlot && <span className="text-slate-500 whitespace-nowrap">{usualSlot}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-slate-400 tabular-nums">
                        ดู <span className="text-white font-semibold">{anime.watchedEp}</span>/{anime.latestEp}
                      </span>
                      {behind > 0 && (
                        <span className="px-1.5 py-[3px] rounded-full bg-amber-400/15 text-amber-300 font-semibold leading-none">
                          ค้างดู {behind} ตอน
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-between gap-1">
                    {primarySource && (
                      <a
                        href={primarySource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={`เปิดดูบน ${primarySource.name}`}
                        className="w-7 h-7 rounded-lg grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] text-slate-300"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(anime);
                      }}
                      title="แก้ไข"
                      className="w-7 h-7 rounded-lg grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] text-slate-300"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Unknown broadcast day */}
      <section className="glass-panel rounded-3xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl grid place-items-center bg-teal-500/10 text-teal-300 shrink-0">
            <HelpCircle className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-white">
              ยังไม่ทราบวันฉาย
              <span className="ml-2 text-sm font-normal text-slate-500">{unknownDayAnime.length} เรื่อง</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              ระบบจะเช็คตอนใหม่จากเว็บให้ หรือเลือกวันเพื่อย้ายขึ้นตาราง
            </p>
          </div>
        </div>

        {unknownDayAnime.length === 0 ? (
          <div className="mt-4 py-6 text-center text-slate-500 text-sm">ทุกเรื่องระบุวันฉายครบแล้ว 🎉</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-4">
            {unknownDayAnime.map((anime) => {
              const primarySource = anime.sources?.[0];
              return (
                <div
                  key={anime.id}
                  className="rounded-2xl p-2.5 flex gap-3 bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-colors"
                >
                  <button type="button" onClick={() => onOpenDetail(anime)} className="shrink-0">
                    <Thumb anime={anime} className="w-14 h-[74px]" />
                  </button>
                  <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <h4
                        onClick={() => onOpenDetail(anime)}
                        className="cursor-pointer text-[13px] font-medium text-white hover:text-teal-200 line-clamp-1"
                      >
                        {anime.title}
                      </h4>
                      <BellToggle anime={anime} onToggle={onToggleNotify} />
                    </div>
                    <div className="flex items-center gap-2 text-[11px] -mt-1">
                      <EpisodeCounts anime={anime} />
                      <span className="text-slate-600">·</span>
                      <span className="text-slate-500">{anime.audioType}</span>
                      <span className="text-slate-600">·</span>
                      <SeasonTag anime={anime} />
                      <MalScoreBadge
                        title={anime.malTitle || anime.title}
                        year={anime.malTitle ? 0 : anime.year}
                        fallback={anime.score}
                        compact
                        className="text-[10px] ml-auto"
                      />
                    </div>
                    {anime.detectedDayNote && (
                      <div className="text-[10.5px] text-amber-300/90 flex items-center gap-1 min-w-0">
                        <Sparkles className="w-3 h-3 shrink-0" />
                        <span className="truncate">{anime.detectedDayNote}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-auto">
                      <select
                        value={anime.broadcastDay}
                        onChange={(e) => onChangeBroadcastDay(anime.id, e.target.value as BroadcastDay)}
                        aria-label="เลือกวันออกอากาศ"
                        className="h-7 min-w-0 flex-1 bg-white/[0.04] border border-white/[0.08] text-slate-200 text-[11px] rounded-lg px-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                      >
                        <option value="UNKNOWN">เลือกวันฉาย…</option>
                        {weekDays.map((wd) => (
                          <option key={wd.id} value={wd.id}>
                            {wd.nameTh}
                          </option>
                        ))}
                      </select>
                      {primarySource && (
                        <a
                          href={primarySource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`เปิดดูบน ${primarySource.name}`}
                          className="w-7 h-7 rounded-lg grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] text-slate-300"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => onEdit(anime)}
                        title="แก้ไข"
                        className="w-7 h-7 rounded-lg grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] text-slate-300"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Finished */}
      {finishedAnime.length > 0 && (
        <section className="glass-panel rounded-3xl p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl grid place-items-center bg-emerald-500/10 text-emerald-300 shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </span>
            <h3 className="text-base sm:text-lg font-semibold text-white">
              ฉายจบแล้ว
              <span className="ml-2 text-sm font-normal text-slate-500">{finishedAnime.length} เรื่อง</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-4">
            {finishedAnime.map((anime) => {
              const primarySource = anime.sources?.[0];
              const behind = behindOf(anime);
              return (
                <div
                  key={anime.id}
                  onClick={() => onOpenDetail(anime)}
                  className="cursor-pointer rounded-2xl p-2.5 flex gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] transition-colors"
                >
                  <Thumb anime={anime} className="w-12 h-16" />
                  <div className="min-w-0 flex-1 flex flex-col justify-between gap-1">
                    <div className="flex items-start gap-1.5">
                      <h4 className="text-[13px] font-medium text-white line-clamp-1 flex-1">{anime.title}</h4>
                      <span
                        className={`px-1 rounded text-[9.5px] font-bold text-white bg-gradient-to-br shrink-0 ${TIER_CONFIGS[anime.grade].gradient}`}
                      >
                        {anime.grade}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px]">
                      <span className="text-slate-400 tabular-nums">
                        ดู <span className="text-white font-semibold">{anime.watchedEp}</span>/
                        {Math.max(Number(anime.totalEp) || 0, Number(anime.latestEp) || 0)}
                      </span>
                      {behind > 0 ? (
                        <span className="px-1.5 py-[3px] rounded-full bg-amber-400/15 text-amber-300 font-semibold leading-none">
                          เหลือ {behind} ตอน
                        </span>
                      ) : (
                        <span className="text-emerald-400 font-medium">✓ ดูจบแล้ว</span>
                      )}
                      <span className="ml-auto text-[10.5px]">
                        <SeasonTag anime={anime} />
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-between gap-1">
                    {primarySource && (
                      <a
                        href={primarySource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={`เปิดดูบน ${primarySource.name}`}
                        className="w-7 h-7 rounded-lg grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] text-slate-300"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(anime);
                      }}
                      title="แก้ไข"
                      className="w-7 h-7 rounded-lg grid place-items-center bg-white/[0.04] hover:bg-white/[0.09] text-slate-300"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
};
